import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import beginHostedCheckout from '@cartwhl/common/api/beginHostedCheckout';
import * as authStore from '@cartwhl/common/stores/authStore';
import * as navigationStore from '@cartwhl/common/stores/navigationStore';
import * as noticesStore from '@cartwhl/common/stores/noticesStore';
import * as productStore from '@cartwhl/common/stores/productStore';

import { StripePrice } from '../types';
import { getStripeProductID } from '../utils/getStripeProductID';
import handleGenericErrors from '../utils/handleGenericErrors';

type CheckoutStoreState = {
  price: StripePrice | null;
  quantity: number;
};

export const state = proxy<CheckoutStoreState>({
  price: null,
  quantity: 1,
});

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'Checkout', enabled: true });
}

///// Actions

export function select(price: StripePrice, quantity: number = 1) {
  state.price = price;
  state.quantity = quantity;
}

export async function fillPrice() {
  const params = new URLSearchParams(window.location.search);

  if (productStore.state.loading) {
    while (productStore.state.loading) {
      await setTimeout(() => {}, 100);
    }
  } else {
    await productStore.fetchProducts(); // TODO: improve perf
  }
  const priceID = params.get('priceID');
  const productID = params.get('productID');
  const seatsParam = params.get('seats');
  const quantity = parseInt(seatsParam || params.get('quantity') || '1', 10);

  state.quantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  const fetchProductID = productID || getStripeProductID('pro');

  if (typeof priceID === 'string') {
    state.price = productStore.getPriceByID(priceID);
  } else if (typeof fetchProductID === 'string') {
    const product = productStore.getProductByID(fetchProductID);
    if (product) {
      state.price = productStore.getPriceByID(product.default_price);
    } else {
      // TODO: Unknown case — URL had a productID that does not exist.
      noticesStore.error(
        'Unable to find a product based on your purchase selection.',
      );
    }
  } else {
    // TODO: Unknown case — try to come up with a price
    // based on the users's status?
    noticesStore.error('Unable to find a product based on your query.');
  }
}

export async function startCheckout() {
  const params = new URLSearchParams();

  if (authStore.state.authStatus === 'anonymous') {
    navigationStore.navigate(
      `sign-up?redirectTo=/checkout?${params.toString()}`,
    );
    return state;
  }

  if (state.price) {
    const stripeHostedCheckoutURL = await completeHostedCheckout(
      state.price,
      state.quantity,
    );
    if (stripeHostedCheckoutURL) {
      window.location.href = stripeHostedCheckoutURL;
    }
  } else {
    noticesStore.error(
      'Unable to initiate your purchase. Please refresh and try again.',
    );
  }

  return state;
}

export async function completeHostedCheckout(
  price: StripePrice | null,
  quantity: number,
) {
  if (!price) {
    noticesStore.info('Unexpected error. Please go back and try again.');
    return;
  }

  const { data, error } = await beginHostedCheckout(price, quantity);

  if (error) {
    await handleGenericErrors(error);
  } else if (data?.url) {
    return data.url;
  } else {
    noticesStore.error('Something went wrong, please try again later!');
    return '';
  }
}

///// Helpers

export const formatMoney = (price: StripePrice, quantity: number = 1) => {
  let output = '';
  if (price.currency === 'usd') {
    output += '$';
  }
  if (typeof price.unit_amount === 'number') {
    output += (price.unit_amount * quantity) / 100;
  }
  return output;
};
